import { jidIdentity } from "@/jidUtils"
import { normalizePlate } from "@/lib/plates"
import type {
  FleetReminderWorkflow,
  ScheduledWorkflow,
  WorkflowJob,
} from "@/reducers/openclawWorkflowsSlice"

/**
 * What a driver room is about: the room itself, the driver sitting in it and
 * the truck they drive.
 *
 * Both fields arrive canonical: JIDs through `jidIdentity`, plates through
 * `normalizePlate`. Built by `selectChatAutomationScope`.
 */
export type ChatAutomationScope = {
  jids: string[]
  plates: string[]
}

/**
 * The automations a driver room answers for, split by where they come from.
 *
 * A rule that names nobody runs for the whole fleet, so it runs for this truck
 * too and the room says so. Only a rule that names *another* truck or driver is
 * left out — it is somebody else's business.
 */
export type ChatWorkflows = {
  /** Rules that name this room, its driver or its truck. */
  forThisDriver: ScheduledWorkflow[]
  /** Rules with no target: every truck is covered, this one included. */
  fleetWide: ScheduledWorkflow[]
  /**
   * Fleet reminders trimmed to this truck's rows — a view for rendering, never
   * something to write back, or the other trucks' rows would be lost.
   */
  fleetReminders: FleetReminderWorkflow[]
}

export const workflowsForChat = (
  jobs: WorkflowJob[],
  scope: ChatAutomationScope,
): ChatWorkflows => {
  const jids = new Set(scope.jids)
  const plates = new Set(scope.plates)

  const result: ChatWorkflows = {
    forThisDriver: [],
    fleetWide: [],
    fleetReminders: [],
  }

  for (const job of jobs) {
    switch (job.kind) {
      case "fleet-reminder": {
        const entries = job.entries.filter((entry) =>
          plates.has(normalizePlate(entry.plate)),
        )
        if (entries.length > 0) result.fleetReminders.push({ ...job, entries })
        break
      }
      case "cron": {
        const target = job.schedule?.targetJid
        if (!target) result.fleetWide.push(job)
        else if (jids.has(jidIdentity(target))) result.forThisDriver.push(job)
        break
      }
    }
  }

  return result
}
